/*
  Help me choose (snippets/mm-choose-start.liquid and sections/mm-choose.liquid).

  Starter: a shopper ticks two to four pieces from the browser wishlist (plus the piece
  on the page, on a product page), gets a link and sends it on WhatsApp.
  Poll page: whoever opens the link taps the piece they like. No login, nothing to type.
  The asker's browser keeps an owner key, so the same link shows them the votes and an
  "Add to tote" button for the favourite.

  Service: POST text/plain JSON to the mm-wishlist-sync Netlify site, /api/choose
  (netlify/functions/choose.mjs). Titles, prices and images on the poll page come from
  the service, which reads them from the store, never from this browser.

  Browser storage (all wrapped in try/catch):
    mm_choose_owner   { pollId: { key, at } }   owner keys, dropped after 15 days
    mm_choose_voter   random id, one per browser, only ever sent with a vote
    mm_choose_votes   { pollId: { handle, at } } this browser's own picks

  Wishlist API used: window.CuratorWishlist.getAll() and the document event
  "wishlist:changed" (snippets/curator-wishlist.liquid). Items are
  { id, variant_id, handle, title, url, image, price (paise), compare_at_price }.

  Lines added to the tote carry the hidden property _mm_source: help-me-choose.
*/
(function () {
  'use strict';

  var DEFAULT_API = 'https://mm-wishlist-sync.netlify.app/api/choose';
  var MIN_PICKS = 2;
  var MAX_PICKS = 4;
  var OWNER_KEY = 'mm_choose_owner';
  var VOTER_KEY = 'mm_choose_voter';
  var VOTES_KEY = 'mm_choose_votes';
  var KEEP_MS = 15 * 24 * 60 * 60 * 1000;

  /* ---------- Pure helpers (tested with node, see the workstream report) ---------- */

  /* First names only: letters, spaces and dots, 24 characters at most. Same rule as the service. */
  function cleanName(value) {
    var text = String(value == null ? '' : value);
    try {
      text = text.normalize('NFC').replace(new RegExp('[^\\p{L}\\p{M} .]', 'gu'), '');
    } catch (e) {
      text = text.replace(/[^A-Za-z .]/g, '');
    }
    return text.replace(/\s+/g, ' ').trim().slice(0, 24).trim();
  }

  /* Paise to the store's money format (shop.money_format, today "₹{{amount_no_decimals}}"),
     without trailing zeros: 149900 -> "₹1,499", matching every server-rendered price.
     A no_decimals format rounds to whole rupees, as Shopify's money filter does.
     With no usable format the rupee sign is used, never "Rs.". */
  function formatMoney(paise, format) {
    var value = parseInt(paise, 10);
    if (isNaN(value)) return '';
    var pattern = String(format || '').replace(/<[^>]*>/g, '');
    var token = /\{\{\s*(\w+)\s*\}\}/.exec(pattern);
    var noDecimals = !!(token && /no_decimals/.test(token[1]));
    var whole = noDecimals || value % 100 === 0;
    var amount = (noDecimals ? Math.round(value / 100) : value / 100).toLocaleString('en-IN', {
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2
    });
    if (!token) return '₹' + amount;
    return pattern.replace(token[0], amount);
  }

  function pollIdFromSearch(search) {
    var match = /[?&]p=([^&#]*)/.exec(String(search || ''));
    if (!match) return null;
    var id;
    try { id = decodeURIComponent(match[1]); } catch (e) { return null; }
    return /^[A-Za-z0-9_-]{10}$/.test(id) ? id : null;
  }

  /* template: optional locale sentence with a [link] placeholder (mm.choose.start.share_message). */
  function shareText(link, template) {
    if (template && template.indexOf('[link]') > -1) return template.replace('[link]', link);
    return 'Help me choose between these from Modus & Mood: ' + link;
  }

  function whatsappUrl(link, template) {
    return 'https://wa.me/?text=' + encodeURIComponent(shareText(link, template));
  }

  /* The piece on the page first, then the wishlist, one entry per handle. */
  function candidates(wishlist, current) {
    var seen = {};
    var out = [];
    (current ? [current] : []).concat(Array.isArray(wishlist) ? wishlist : []).forEach(function (item) {
      if (!item || typeof item.handle !== 'string' || !item.handle || seen[item.handle]) return;
      seen[item.handle] = true;
      out.push({
        handle: item.handle,
        title: String(item.title || item.handle),
        image: item.image ? String(item.image) : '',
        price: item.price,
        url: item.url ? String(item.url) : '/products/' + item.handle
      });
    });
    return out;
  }

  /* Tick or untick a handle. A fifth tick is refused and the list comes back unchanged. */
  function toggleSelection(selected, handle, max) {
    var limit = max || MAX_PICKS;
    var list = selected.slice();
    var at = list.indexOf(handle);
    if (at > -1) {
      list.splice(at, 1);
      return { selected: list, refused: false };
    }
    if (list.length >= limit) return { selected: list, refused: true };
    list.push(handle);
    return { selected: list, refused: false };
  }

  /* The piece with the most votes. No favourite while there are no votes or the top is shared. */
  function leader(products) {
    var top = 0;
    var handles = [];
    (products || []).forEach(function (p) {
      var votes = p.votes || 0;
      if (votes > top) { top = votes; handles = [p.handle]; } else if (votes === top && votes > 0) handles.push(p.handle);
    });
    return { handle: handles.length === 1 ? handles[0] : null, tie: handles.length > 1, votes: top };
  }

  function percent(votes, total) {
    if (!total) return 0;
    return Math.round(((votes || 0) / total) * 100);
  }

  /* one, other: optional locale phrases (mm.choose.poll.votes); other carries {count}. */
  function votesLabel(count, one, other) {
    if (count === 1) return one || '1 vote';
    return (other || '{count} votes').replace('{count}', String(count || 0));
  }

  /* {name} style placeholders in poll page phrases rendered from the locale file. */
  function fillIn(template, values) {
    return String(template == null ? '' : template).replace(/\{(\w+)\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values || {}, key) ? values[key] : match;
    });
  }

  /* Shopify's image CDN resizes on a width parameter. Other hosts are left alone. */
  function sizedImage(url, width) {
    if (!url || !/^https:\/\/[^/]*(cdn\.shopify\.com|modusandmood\.com)\//.test(url)) return url || '';
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$1' + width);
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  /* Drops remembered polls older than 15 days (links close after 14). */
  function pruneMap(map, now) {
    var out = {};
    Object.keys(map && typeof map === 'object' ? map : {}).forEach(function (id) {
      var entry = map[id];
      if (entry && typeof entry === 'object' && now - (entry.at || 0) < KEEP_MS) out[id] = entry;
    });
    return out;
  }

  function firstAvailableVariant(product) {
    var variants = (product && product.variants) || [];
    for (var i = 0; i < variants.length; i++) if (variants[i].available) return variants[i];
    return null;
  }

  var helpers = {
    cleanName: cleanName,
    formatMoney: formatMoney,
    pollIdFromSearch: pollIdFromSearch,
    shareText: shareText,
    whatsappUrl: whatsappUrl,
    candidates: candidates,
    toggleSelection: toggleSelection,
    leader: leader,
    percent: percent,
    votesLabel: votesLabel,
    fillIn: fillIn,
    sizedImage: sizedImage,
    pruneMap: pruneMap,
    firstAvailableVariant: firstAvailableVariant
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = helpers;
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (window.mmChooseReady) return;
  window.mmChooseReady = true;

  /* ---------- Browser plumbing ---------- */

  function readMap(key) {
    try {
      var map = JSON.parse(localStorage.getItem(key) || '{}');
      return pruneMap(map, Date.now());
    } catch (e) { return {}; }
  }
  function writeMap(key, map) {
    try { localStorage.setItem(key, JSON.stringify(pruneMap(map, Date.now()))); } catch (e) { /* storage blocked */ }
  }
  function remember(key, id, entry) {
    var map = readMap(key);
    entry.at = Date.now();
    map[id] = entry;
    writeMap(key, map);
  }

  var sessionVoter = null;
  function voterId() {
    try {
      var saved = localStorage.getItem(VOTER_KEY);
      if (saved && /^[A-Za-z0-9_-]{8,64}$/.test(saved)) return saved;
    } catch (e) { /* fall through */ }
    if (!sessionVoter) {
      sessionVoter = '';
      try {
        var bytes = new Uint8Array(12);
        window.crypto.getRandomValues(bytes);
        for (var i = 0; i < bytes.length; i++) sessionVoter += ('0' + bytes[i].toString(16)).slice(-2);
      } catch (e) {
        sessionVoter = 'v' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
      }
    }
    try { localStorage.setItem(VOTER_KEY, sessionVoter); } catch (e) { /* storage blocked */ }
    return sessionVoter;
  }

  function api(endpoint, body) {
    return fetch(endpoint || DEFAULT_API, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (data) {
        return { status: r.status, ok: r.ok, body: data || {} };
      });
    });
  }

  /* Customer events for the custom pixel. Never allowed to break the page. */
  function track(name, data) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, data || {});
      }
    } catch (e) { /* ignore */ }
  }

  function readConfig(root) {
    try { return JSON.parse(root.querySelector('[data-mm-choose-config]').textContent) || {}; } catch (e) { return {}; }
  }

  /* Starter words from the locale file (mm.choose.start), rendered by
     snippets/mm-choose-start.liquid into [data-mm-i18n]. Liquid's t filter HTML-escapes,
     so each string is decoded once here. Missing keys fall back to the English in word(). */
  function decodeEntities(text) {
    var box = document.createElement('textarea');
    box.innerHTML = text;
    return box.value;
  }
  function readI18n(root) {
    var out = {};
    try {
      var raw = JSON.parse(root.querySelector('[data-mm-i18n]').textContent) || {};
      Object.keys(raw).forEach(function (key) {
        var value = raw[key];
        if (typeof value === 'string') out[key] = decodeEntities(value);
        else if (Array.isArray(value)) out[key] = value.map(function (v) { return typeof v === 'string' ? decodeEntities(v) : ''; });
      });
    } catch (e) { /* English fallbacks */ }
    return out;
  }
  function wordFrom(strings) {
    return function (key, fallback) {
      var value = strings[key];
      return typeof value === 'string' && value && value.indexOf('translation missing') === -1 ? value : fallback;
    };
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (name) {
      var value = attrs[name];
      if (value === null || value === undefined || value === false) return;
      if (name === 'text') node.textContent = value;
      else if (name === 'class') node.className = value;
      else node.setAttribute(name, value === true ? '' : value);
    });
    (children || []).forEach(function (child) { if (child) node.appendChild(child); });
    return node;
  }

  function copyText(text, input) {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      return navigator.clipboard.writeText(text).then(function () { return true; }, function () { return legacyCopy(input); });
    }
    return Promise.resolve(legacyCopy(input));
  }
  function legacyCopy(input) {
    try {
      if (!input) return false;
      input.focus();
      input.select();
      return document.execCommand('copy');
    } catch (e) { return false; }
  }

  /* Wires a Copy, WhatsApp and (where the phone offers it) native share row to one link. */
  /* Until the refresh theme is published, the poll page exists only on it. A link opened in a fresh browser has no
     preview cookie and lands on the live theme, which has no poll page, so preview links carry the theme id. */
  function previewSafe(link) {
    try {
      var t = window.Shopify && window.Shopify.theme;
      if (!t || t.role === 'main' || !t.id || /preview_theme_id=/.test(link)) return link;
      return link + (link.indexOf('?') === -1 ? '?' : '&') + 'preview_theme_id=' + t.id;
    } catch (e) { return link; }
  }

  /* strings: optional { share_message, share_title, share_text, copied, copy_hint } from the
     caller's locale block; each falls back to the English below. */
  function wireShare(scope, link, where, strings) {
    var word = wordFrom(strings || {});
    link = previewSafe(link);
    var input = scope.querySelector('[data-mm-choose-link]');
    var copy = scope.querySelector('[data-mm-choose-copy]');
    var whatsapp = scope.querySelector('[data-mm-choose-whatsapp]');
    var native = scope.querySelector('[data-mm-choose-share]');
    var note = scope.querySelector('[data-mm-choose-share-msg]');
    if (input) input.value = link;
    if (whatsapp) {
      whatsapp.setAttribute('href', whatsappUrl(link, word('share_message', '')));
      whatsapp.onclick = function () { track('mm_choose_share', { method: 'whatsapp', where: where }); };
    }
    if (copy) {
      copy.onclick = function () {
        copyText(link, input).then(function (done) {
          if (note) note.textContent = done ? word('copied', 'Link copied. Paste it into any chat.') : word('copy_hint', 'Press and hold the link to copy it.');
          if (done) track('mm_choose_share', { method: 'copy', where: where });
        });
      };
    }
    if (native) {
      if (typeof navigator.share === 'function') {
        native.hidden = false;
        native.onclick = function () {
          navigator.share({ title: word('share_title', 'Help me choose'), text: word('share_text', 'Help me choose between these from Modus & Mood'), url: link })
            .then(function () { track('mm_choose_share', { method: 'native', where: where }); })
            .catch(function () { /* closed without sharing */ });
        };
      } else {
        native.hidden = true;
      }
    }
  }

  /* ---------- Starter: "Can't decide? Ask someone" ---------- */

  function initStarter(root) {
    var cfg = readConfig(root);
    var strings = readI18n(root);
    var word = wordFrom(strings);
    var openBtn = root.querySelector('[data-mm-choose-open]');
    var panel = root.querySelector('[data-mm-choose-panel]');
    var listEl = root.querySelector('[data-mm-choose-list]');
    var hint = root.querySelector('[data-mm-choose-hint]');
    var form = root.querySelector('[data-mm-choose-form]');
    var count = root.querySelector('[data-mm-choose-count]');
    var nameInput = root.querySelector('[data-mm-choose-asker]');
    var createBtn = root.querySelector('[data-mm-choose-create]');
    var msg = root.querySelector('[data-mm-choose-msg]');
    var result = root.querySelector('[data-mm-choose-result]');
    var votesLink = root.querySelector('[data-mm-choose-votes]');
    if (!openBtn || !panel || !listEl || !createBtn) return;

    var current = cfg.product && cfg.product.handle ? cfg.product : null;
    var selected = current ? [current.handle] : [];
    var busy = false;

    function wishlist() {
      try { return window.CuratorWishlist ? window.CuratorWishlist.getAll() : []; } catch (e) { return []; }
    }
    function pieces() { return candidates(wishlist(), current); }

    function syncCount() {
      var full = selected.length >= MAX_PICKS;
      if (count) {
        var picked = strings.picked && strings.picked[selected.length];
        count.textContent = full
          ? word('picked_full', 'Four picked, which is the most one link can hold. Untick one to swap.')
          : (picked && picked.indexOf('translation missing') === -1 ? picked : selected.length + ' picked. Choose two to four.');
      }
      listEl.querySelectorAll('input[type="checkbox"]').forEach(function (box) {
        box.disabled = busy || (full && !box.checked);
      });
      createBtn.disabled = busy || selected.length < MIN_PICKS;
    }

    function renderList() {
      var list = pieces();
      var known = {};
      list.forEach(function (p) { known[p.handle] = true; });
      selected = selected.filter(function (h) { return known[h]; });
      listEl.textContent = '';
      list.forEach(function (piece, index) {
        var id = (cfg.uid || 'MmChoose') + '-pick-' + index;
        var box = el('input', { type: 'checkbox', id: id, class: 'mm-choose-start__box', value: piece.handle });
        box.checked = selected.indexOf(piece.handle) > -1;
        box.addEventListener('change', function () {
          var next = toggleSelection(selected, piece.handle, MAX_PICKS);
          selected = next.selected;
          box.checked = selected.indexOf(piece.handle) > -1;
          if (result) result.hidden = true;
          if (msg) msg.textContent = '';
          syncCount();
        });
        var thumb = piece.image
          ? el('img', { class: 'mm-choose-start__thumb', src: sizedImage(piece.image, 120), alt: '', width: '56', height: '56', loading: 'lazy', decoding: 'async' })
          : el('span', { class: 'mm-choose-start__thumb mm-choose-start__thumb--blank', 'aria-hidden': 'true' });
        listEl.appendChild(el('li', { class: 'mm-choose-start__item' }, [
          el('label', { class: 'mm-choose-start__label', for: id }, [
            box,
            thumb,
            el('span', { class: 'mm-choose-start__text' }, [
              el('span', { class: 'mm-choose-start__title', text: piece.title }),
              el('span', { class: 'mm-choose-start__price', text: formatMoney(piece.price, cfg.money_format) })
            ])
          ])
        ]));
      });
      var enough = list.length >= MIN_PICKS;
      if (hint) {
        hint.hidden = enough;
        hint.textContent = enough ? '' : (current
          ? word('hint_product', 'Tap the heart on one more piece you like, then come back here to ask.')
          : word('hint_wishlist', 'Save at least two pieces with the heart, then come back here to ask.'));
      }
      if (form) form.hidden = !enough;
      syncCount();
    }

    function sync() {
      root.hidden = pieces().length === 0;
      if (!panel.hidden) renderList();
    }

    openBtn.addEventListener('click', function () {
      var open = panel.hidden;
      panel.hidden = !open;
      openBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) { renderList(); track('mm_choose_open', { where: current ? 'product' : 'wishlist' }); }
    });

    function create() {
      if (busy || selected.length < MIN_PICKS) return;
      busy = true;
      var label = createBtn.textContent;
      createBtn.textContent = word('creating', 'Getting your link');
      createBtn.setAttribute('aria-busy', 'true');
      if (msg) msg.textContent = '';
      syncCount();
      api(cfg.endpoint, { action: 'create', handles: selected.slice(), asker: cleanName(nameInput && nameInput.value) })
        .then(function (res) {
          if (res.ok && res.body.id && res.body.owner_key && res.body.share_url) {
            remember(OWNER_KEY, res.body.id, { key: res.body.owner_key });
            if (votesLink) votesLink.setAttribute('href', (cfg.page_url || '/pages/choose') + '?p=' + encodeURIComponent(res.body.id));
            if (result) {
              result.hidden = false;
              wireShare(result, res.body.share_url, 'starter', strings);
            }
            if (msg) msg.textContent = word('ready', 'Your link is ready. It stays open for 14 days.');
            track('mm_choose_link_created', { pieces: selected.length, where: current ? 'product' : 'wishlist' });
            return;
          }
          var text = word('error_generic', 'We could not make your link just now. Please try again.');
          if (res.status === 429) text = word('error_limit', 'That is a lot of links in a short while. Please try again in an hour.');
          if (res.status === 400 && res.body.error === 'unknown product') {
            var gone = pieces().filter(function (p) { return p.handle === res.body.handle; })[0];
            text = gone
              ? word('error_gone', '[title] is no longer in the store. Untick it and try again.').replace('[title]', gone.title)
              : word('error_gone_unknown', 'One of these pieces is no longer in the store. Untick it and try again.');
          }
          if (msg) msg.textContent = text;
        })
        .catch(function () {
          if (msg) msg.textContent = word('error_network', 'We could not reach the link service. Check your connection and try again.');
        })
        .then(function () {
          busy = false;
          createBtn.textContent = label;
          createBtn.removeAttribute('aria-busy');
          syncCount();
        });
    }
    createBtn.addEventListener('click', create);
    if (nameInput) {
      nameInput.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') { event.preventDefault(); create(); }
      });
    }

    document.addEventListener('wishlist:changed', sync);
    sync();
  }

  /* ---------- Poll page ---------- */

  /* Poll page words come from the locale file (mm.choose.poll), rendered by
     sections/mm-choose.liquid into [data-mm-i18n]; word() falls back to the English here.
     Phrases carry {title}, {name}, {count}, {date} or {names}, filled by fillIn(). */
  function initPoll(root) {
    var cfg = readConfig(root);
    var word = wordFrom(readI18n(root));
    var heading = root.querySelector('[data-mm-choose-heading]');
    var sub = root.querySelector('[data-mm-choose-sub]');
    var live = root.querySelector('[data-mm-choose-live]');
    var nameRow = root.querySelector('[data-mm-choose-name]');
    var nameInput = root.querySelector('[data-mm-choose-voter]');
    var grid = root.querySelector('[data-mm-choose-grid]');
    var state = root.querySelector('[data-mm-choose-state]');
    var stateTitle = root.querySelector('[data-mm-choose-state-title]');
    var stateText = root.querySelector('[data-mm-choose-state-text]');
    var retry = root.querySelector('[data-mm-choose-retry]');
    var ownerBox = root.querySelector('[data-mm-choose-owner]');
    var refresh = root.querySelector('[data-mm-choose-refresh]');
    var welcome = root.querySelector('[data-mm-choose-welcome]');
    if (!grid) return;

    var id = pollIdFromSearch(window.location.search);
    var root_url = cfg.root_url || '/';
    var cards = {};
    var poll = null;
    var isOwner = false;
    var myVote = null;
    var voting = false;
    var loading = false;

    function say(text) { if (live) live.textContent = text; }

    function showState(kind) {
      var copy = {
        empty: [word('empty_title', 'Nothing to choose yet'), word('empty_text', 'This page opens from a link that someone sends you. If you would like help picking, save a few pieces with the heart, open your wishlist and tap "Can\'t decide? Ask someone".')],
        closed: [word('closed_title', 'This link has closed'), word('closed_text', 'Links stay open for 14 days, and this one has either closed or was not copied in full. Ask for a fresh link, or have a look around the store.')],
        busy: [word('busy_title', 'One moment'), word('busy_text', 'This page has been opened a lot in the last few minutes. Please wait a little and try again.')],
        error: [word('error_title', 'We could not load this'), word('error_text', 'Something went wrong on our side or the connection dropped. Please try again.')]
      }[kind];
      grid.hidden = true;
      grid.removeAttribute('aria-busy');
      if (nameRow) nameRow.hidden = true;
      if (ownerBox) ownerBox.hidden = true;
      if (welcome) welcome.hidden = true;
      if (sub) sub.hidden = true;
      if (heading) heading.textContent = copy[0];
      if (stateTitle) stateTitle.textContent = copy[0];
      if (stateText) stateText.textContent = copy[1];
      if (retry) retry.hidden = !(kind === 'error' || kind === 'busy');
      if (state) state.hidden = false;
      root.setAttribute('data-mm-choose-view', kind);
    }

    function buildCards(products) {
      grid.textContent = '';
      cards = {};
      grid.setAttribute('data-count', String(products.length));
      products.forEach(function (p) {
        var href = root_url + 'products/' + encodeURIComponent(p.handle);
        var button = el('button', { type: 'button', class: 'mm-btn mm-btn--solid mm-btn--full mm-choose__pick', text: word('this_one', 'This one') });
        var bar = el('span', { class: 'mm-choose__bar-fill' });
        var tallyText = el('span', { class: 'mm-choose__tally-text' });
        var tally = el('div', { class: 'mm-choose__tally', hidden: true }, [
          el('span', { class: 'mm-choose__bar', 'aria-hidden': 'true' }, [bar]),
          tallyText
        ]);
        var names = el('p', { class: 'mm-choose__names', hidden: true });
        var note = el('p', { class: 'mm-choose__note', role: 'status', 'aria-live': 'polite' });
        var media = p.image
          ? el('img', { class: 'mm-choose__img', src: sizedImage(p.image, 600), alt: p.title, width: '600', height: '600', loading: 'lazy', decoding: 'async' })
          : el('span', { class: 'mm-choose__img mm-choose__img--blank', 'aria-hidden': 'true' });
        var card = el('li', { class: 'mm-choose__card' }, [
          el('div', { class: 'mm-choose__media' }, [media]),
          el('h2', { class: 'mm-choose__title', text: p.title }),
          el('p', { class: 'mm-choose__price', text: formatMoney(p.price, cfg.money_format) }),
          tally,
          names,
          button,
          note,
          el('a', { class: 'mm-choose__more', href: href, text: word('see_piece', 'See this piece'), 'aria-label': fillIn(word('see_piece_label', 'See {title}'), { title: p.title }) })
        ]);
        button.addEventListener('click', function () { if (isOwner) addToTote(p, button, note); else vote(p, button); });
        cards[p.handle] = { card: card, button: button, bar: bar, tally: tally, tallyText: tallyText, names: names, note: note };
        grid.appendChild(card);
      });
      grid.hidden = false;
      grid.removeAttribute('aria-busy');
    }

    function votes(count) { return votesLabel(count, word('votes_one', '1 vote'), word('votes_other', '{count} votes')); }

    function paint() {
      if (!poll) return;
      var top = leader(poll.products);
      var showTally = isOwner || !!myVote;
      poll.products.forEach(function (p) {
        var c = cards[p.handle];
        if (!c) return;
        c.tally.hidden = !showTally;
        c.bar.style.width = percent(p.votes, poll.total_votes) + '%';
        c.tallyText.textContent = votes(p.votes);
        c.card.classList.toggle('is-picked', !isOwner && myVote === p.handle);
        c.card.classList.toggle('is-leading', showTally && top.handle === p.handle);
        if (isOwner) {
          var who = (p.voters || []).join(', ');
          c.names.hidden = !who;
          c.names.textContent = who ? fillIn(word('from_names', 'From {names}'), { names: who }) : '';
          c.button.textContent = top.handle === p.handle ? word('add_favourite', 'Add the favourite to tote') : word('add_to_tote', 'Add to tote');
          c.button.className = 'mm-btn mm-btn--full mm-choose__pick ' + (top.handle === p.handle || !top.handle ? 'mm-btn--solid' : 'mm-btn--outline');
        } else if (myVote) {
          var mine = myVote === p.handle;
          c.button.textContent = mine ? word('your_pick', 'Your pick') : word('choose_instead', 'Choose this instead');
          c.button.setAttribute('aria-pressed', mine ? 'true' : 'false');
          c.button.className = 'mm-btn mm-btn--full mm-choose__pick ' + (mine ? 'mm-btn--solid' : 'mm-btn--outline');
        }
      });

      if (isOwner) {
        if (heading) heading.textContent = word('owner_heading', 'Your votes so far');
        var closes = '';
        try { closes = new Date(poll.expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long' }); } catch (e) { /* ignore */ }
        /* Whole sentences per case, so editors never see one built from pieces. */
        var n = poll.total_votes || 0;
        var line;
        if (n === 0) line = word('owner_none', 'No votes yet. Send your link again and give it a little time.');
        else if (top.tie) line = n === 1 ? word('owner_votes_tie_one', '1 vote in. It is a tie at the top so far.') : fillIn(word('owner_votes_tie_other', '{count} votes in. It is a tie at the top so far.'), { count: n });
        else line = n === 1 ? word('owner_votes_one', '1 vote in.') : fillIn(word('owner_votes_other', '{count} votes in.'), { count: n });
        if (sub) sub.textContent = line + (closes ? ' ' + fillIn(word('open_until', 'Your link stays open until {date}.'), { date: closes }) : '');
        if (ownerBox) ownerBox.hidden = false;
        if (nameRow) nameRow.hidden = true;
        if (welcome) welcome.hidden = true;
      } else {
        if (heading) heading.textContent = poll.asker ? fillIn(word('asker_heading', '{name} would like your help choosing'), { name: poll.asker }) : (cfg.heading || 'Help choose between these');
        if (sub) {
          sub.textContent = myVote
            ? word('sub_voted', 'Here is how the votes stand. You can change your pick while the link is open.')
            : word('sub_vote', 'Tap the one you like best. No sign up, nothing to type.');
        }
        if (nameRow) nameRow.hidden = !!myVote;
        if (welcome) welcome.hidden = !myVote;
      }
      root.setAttribute('data-mm-choose-view', isOwner ? 'owner' : (myVote ? 'voted' : 'vote'));
    }

    function vote(product, button) {
      if (voting || !poll || myVote === product.handle) return;
      voting = true;
      var label = button.textContent;
      button.textContent = word('sending', 'Sending');
      button.setAttribute('aria-busy', 'true');
      Object.keys(cards).forEach(function (h) { cards[h].button.disabled = true; });
      api(cfg.endpoint, { action: 'vote', id: id, handle: product.handle, voter_id: voterId(), name: cleanName(nameInput && nameInput.value) })
        .then(function (res) {
          if (res.ok && res.body.found) {
            var moved = !!myVote;
            poll = res.body;
            myVote = product.handle;
            remember(VOTES_KEY, id, { handle: myVote });
            paint();
            var thanks = moved
              ? (poll.asker ? word('vote_moved_asker', 'Your pick has moved to {title}. {name} can see it now.') : word('vote_moved', 'Your pick has moved to {title}.'))
              : (poll.asker ? word('vote_thanks_asker', 'Thank you. You picked {title}. {name} can see it now.') : word('vote_thanks', 'Thank you. You picked {title}.'));
            say(fillIn(thanks, { title: product.title, name: poll.asker || '' }));
            track('mm_choose_vote', { changed: moved });
            return;
          }
          button.textContent = label;
          if (res.status === 404) { showState('closed'); return; }
          say(res.status === 429
            ? word('vote_busy', 'That is a lot of taps in a short while. Please wait a few minutes and try again.')
            : word('vote_error', 'We could not save your pick. Please try again.'));
        })
        .catch(function () {
          button.textContent = label;
          say(word('vote_offline', 'We could not save your pick. Check your connection and try again.'));
        })
        .then(function () {
          voting = false;
          Object.keys(cards).forEach(function (h) { cards[h].button.disabled = false; cards[h].button.removeAttribute('aria-busy'); });
          if (poll && !isOwner && myVote) paint();
          if (document.activeElement === document.body && cards[product.handle]) cards[product.handle].button.focus();
        });
    }

    /* Owner: first available variant, tagged so the order line shows where it came from.
       Opens the drawer the way assets/mm-cart-pairs.js does. A piece that comes in options
       (sizes) is never added blind; the asker is taken to its page to choose. */
    function addToTote(product, button, note) {
      if (button.disabled) return;
      var label = button.textContent;
      var cart = document.querySelector('cart-drawer');
      if (!cart || typeof cart.renderContents !== 'function') cart = null;
      var productUrl = root_url + 'products/' + encodeURIComponent(product.handle);
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = word('adding', 'Adding');
      note.textContent = '';
      fetch(productUrl + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('gone'); return r.json(); })
        .then(function (full) {
          if ((full.variants || []).length > 1) { window.location.href = productUrl; return null; }
          var variant = firstAvailableVariant(full);
          if (!variant) throw new Error('sold out');
          var body = { items: [{ id: variant.id, quantity: 1, properties: { _mm_source: 'help-me-choose' } }] };
          if (cart) {
            body.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
            body.sections_url = window.location.pathname;
          }
          return fetch((cfg.cart_add_url || '/cart/add') + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body)
          }).then(function (r) { return r.json().then(function (data) { return { ok: r.ok, json: data }; }); });
        })
        .then(function (res) {
          if (!res) return;
          if (!res.ok) throw new Error(res.json.description || res.json.message || 'add failed');
          note.textContent = word('added', 'Added to your tote.');
          track('mm_choose_add_to_tote', { handle: product.handle, votes: product.votes || 0 });
          if (cart) {
            var first = res.json.items && res.json.items[0];
            cart.classList.remove('is-empty');
            cart.renderContents({ id: first ? first.product_id : null, sections: res.json.sections });
          } else {
            window.location.href = cfg.cart_url || '/cart';
          }
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'mm-choose' });
          }
        })
        .catch(function (err) {
          var reason = err && err.message;
          if (reason === 'sold out') note.textContent = word('sold_out', 'This piece is sold out right now.');
          else if (reason === 'gone') note.textContent = word('gone', 'This piece is no longer in the store.');
          else if (reason && reason !== 'add failed' && reason !== 'Failed to fetch') note.textContent = reason;
          else note.textContent = word('add_error', 'We could not add that to your tote. Please try again.');
        })
        .then(function () {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = label;
        });
    }

    function load(quiet) {
      if (loading) return;
      if (!id) { showState('empty'); return; }
      loading = true;
      var owners = readMap(OWNER_KEY);
      var ownerKey = owners[id] && owners[id].key;
      var ask = ownerKey
        ? api(cfg.endpoint, { action: 'mine', id: id, owner_key: ownerKey }).then(function (res) {
          /* A key the service does not accept falls back to the public view. */
          if (res.status === 404) return api(cfg.endpoint, { action: 'get', id: id, voter_id: voterId() });
          return res;
        })
        : api(cfg.endpoint, { action: 'get', id: id, voter_id: voterId() });
      ask.then(function (res) {
        if (res.ok && res.body.found && Array.isArray(res.body.products)) {
          var firstPaint = !poll;
          poll = res.body;
          isOwner = !!poll.owner;
          if (!isOwner) {
            var votes = readMap(VOTES_KEY);
            myVote = poll.your_vote || (votes[id] && votes[id].handle) || null;
            if (myVote && !poll.products.some(function (p) { return p.handle === myVote; })) myVote = null;
          }
          if (state) state.hidden = true;
          if (sub) sub.hidden = false;
          if (firstPaint || Object.keys(cards).length !== poll.products.length) buildCards(poll.products);
          paint();
          if (isOwner && ownerBox) wireShare(ownerBox, cfg.share_base + '?p=' + encodeURIComponent(id), 'owner', readI18n(root));
          if (quiet) {
            say(poll.total_votes === 1
              ? word('updated_one', 'Votes updated. 1 vote so far.')
              : fillIn(word('updated_other', 'Votes updated. {count} votes so far.'), { count: poll.total_votes || 0 }));
          }
          return;
        }
        if (quiet) { say(word('refresh_error', 'We could not check for new votes. Please try again.')); return; }
        if (res.status === 404) showState('closed');
        else if (res.status === 429) showState('busy');
        else showState('error');
      }).catch(function () {
        if (quiet) say(word('refresh_offline', 'We could not check for new votes. Check your connection and try again.'));
        else showState('error');
      }).then(function () { loading = false; });
    }

    if (retry) retry.addEventListener('click', function () { window.location.reload(); });
    if (refresh) refresh.addEventListener('click', function () { load(true); });
    /* The asker hops between WhatsApp and this tab; coming back shows fresh votes. */
    var lastLoad = Date.now();
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible' && isOwner && Date.now() - lastLoad > 20000) {
        lastLoad = Date.now();
        load(true);
      }
    });
    load(false);
  }

  function boot() {
    document.querySelectorAll('[data-mm-choose-start]').forEach(initStarter);
    document.querySelectorAll('[data-mm-choose-poll]').forEach(initPoll);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
